/**
 * PourSetup Page — View or edit an existing pour.
 *
 * Routes:
 *   /pours/:id        → view mode (read-only)
 *   /pours/:id/edit   → edit mode
 *
 * Layout: the SAME 4-step wizard as PourCreate.tsx (Function, Pour Details,
 * Hardware / Sensors, Notifications / Alerts) — horizontal brand-green
 * Stepper on desktop, compact progress bar on mobile, and the identical
 * BACK / SAVE-NEXT / Cancel-Return footer — so creating and editing a pour
 * look and feel identical. Unlike create, every step is freely clickable
 * (the pour already exists, so there is no forward gating).
 *
 * Pour-level configuration (functions, details, target/limit groups) is
 * staged locally and committed by the footer's SAVE / NEXT button; the
 * collection sections (locations, contacts, attachments) manage their own
 * rows via dialogs that apply immediately.
 *
 * The pre-restyle layout is preserved verbatim at PourSetupLegacy.tsx
 * (route /pours/:id/edit-legacy) for comparison.
 *
 * Create mode lives in PourCreate.tsx.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Collapse,
  Autocomplete,
  Snackbar,
  useTheme,
  useMediaQuery,
  type SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AddCircleOutline as AddCircleIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  LockOutlined as LockOutlinedIcon,
  Dashboard as DashboardIcon,
  StopCircle as StopCircleIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import {
  poursAPI,
  type PourDetail,
  type PourUpdate,
  type PourLookups,
} from '../services/pours';
import productsService, {
  type Product,
  type ProductListItem,
  type ProductCalibrationLink,
  type TargetGroup,
  type SupplierPlant,
} from '../services/products';
import projectsService from '../services/projects';
import { authAPI } from '../services';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import { apiErrorText } from '../utils/apiError';
import AzureAddressAutocomplete from '../components/AzureAddressAutocomplete';
import { TIMEZONES, type TimezoneOption } from '../data/timezones';
import { collapseProductRevisions } from '../utils/products';
import { labelWithUnit, valueWithUnit } from '../utils/maturityDisplay';
import {
  sameCompany,
  useCompanyStrengthUnit,
  useCompanyStrengthUnits,
} from '../utils/useCompanyStrengthUnit';
import PourLocationsSection from '../components/pour/PourLocationsSection';
import PourContactsSection from '../components/pour/PourContactsSection';
import PourAttachmentsSection from '../components/pour/PourAttachmentsSection';
import CalibrationCheckPanel from '../components/pour/CalibrationCheckPanel';
import { useCalibrationCheck } from '../components/pour/useCalibrationCheck';
import {
  DEFAULT_MATURITY_METHOD,
  MISMATCH_ADVICE_ADDED_MATURITY_LOCKED_TEXT,
  MISMATCH_ADVICE_ADDED_MATURITY_TEXT,
  acknowledgeFlag,
  calibrationCheckBlock,
  describeMethod,
  noCalibrationText,
  pourComputesMaturity,
} from '../utils/calibrationCheck';
import PourFunctionSelector, {
  monitoringFunctionsToString,
  stringToMonitoringFunctions,
  deriveFunctionalityCode,
  type MonitoringFunctionId,
} from '../components/pour/PourFunctionSelector';
import StopMonitoringFields from '../components/pour/StopMonitoringFields';
import {
  STOP_AFTER_FINAL_TARGETS,
  STOP_AT_SET_TIME,
  describeStop,
  stopSettingsErrors,
  xHoursUnit,
} from '../utils/stopMonitoring';
import {
  BRAND_GREEN,
  PRIMARY_ACTION_SX,
  SELECT_MENU_PROPS,
  DROPDOWN_AUTOCOMPLETE_SLOT_PROPS,
  PICKER_POPPER_SX,
  STEPPER_SX,
  mobileProgressSegmentSx,
} from '../components/pour/pourWizardStyles';
import { utcToLocalInput, localInputToUtc, formatInTimeZone, timeZoneLabel } from '../utils/datetime';

const EMPTY_LOOKUPS: PourLookups = {
  projects: [],
  weather_stations: [],
  loggers: [],
  tanks: [],
  contacts: [],
  mass_concrete_defaults: [],
  evaporation_defaults: [],
};

// Same step titles as PourCreate so the two flows read identically.
const STEPS = ['Function', 'Pour Details', 'Hardware / Sensors', 'Notifications / Alerts'];

// Label shown for a mix in the Product/Recipe dropdown (mirrors PourCreate).
const formatProductOption = (p: ProductListItem): string => {
  const parts = [p.code];
  if (p.supplier_plant_name) parts.push(p.supplier_plant_name);
  if (p.description) parts.push(p.description);
  return parts.join(' · ');
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PageMode = 'edit' | 'view';

interface FormData {
  /** Server-generated identifier (HM-yyMM-####). Read-only on edit/view. */
  PourCode: string;
  /** Human-readable pour status from VwPourInfo: 'Pending' | 'In Progress'
   *  | 'Complete' | 'Stop'. Drives Stop-button visibility. Empty on create. */
  PourStatus: string;
  PourName: string;
  Functionality: string;
  ProjectID: string;
  Location: string;
  ProjectRef: string;
  PourVolume: string;
  Levels: string;
  ProductID: string;
  SupplierPlantID: string;
  TargetGroupID: string;
  LimitGroupID: string;
  EvaLimitGroupID: string;
  WeatherStationID: string;
  CalibrationID: string;
  MaturityMethod: string;
  ActivationEnergy: string;
  StopType: string;
  XHours: string;
  PreSetStopTime: string;
  Started: string;
  Finished: string;
}

const INITIAL_FORM_DATA: FormData = {
  PourCode: '',
  PourStatus: '',
  PourName: '',
  Functionality: '1',
  ProjectID: '',
  Location: '',
  ProjectRef: '',
  PourVolume: '',
  Levels: '',
  ProductID: '',
  SupplierPlantID: '',
  TargetGroupID: '',
  LimitGroupID: '',
  EvaLimitGroupID: '',
  WeatherStationID: '',
  CalibrationID: '',
  MaturityMethod: '',
  ActivationEnergy: '',
  StopType: '',
  XHours: '',
  PreSetStopTime: '',
  Started: '',
  Finished: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function usePageMode(): { mode: PageMode; pourId: string | undefined } {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return location.pathname.endsWith('/edit')
    ? { mode: 'edit', pourId: id }
    : { mode: 'view', pourId: id };
}

/** Map a pour status to a Chip colour (case-insensitive — the API returns
 *  'In Progress' with a capital P, which the old check missed). */
const statusChipColor = (status?: string): 'success' | 'warning' | 'default' => {
  switch ((status ?? '').toLowerCase()) {
    case 'complete':
      return 'success';
    case 'in progress':
      return 'warning';
    default:
      return 'default';
  }
};

/** Display label for a pour status — DB stores 'Complete', UI shows 'Completed'. */
const statusChipLabel = (status: string): string =>
  status.toLowerCase() === 'complete' ? 'Completed' : status;

const lookupName = (
  items: { id: string; name: string }[] | undefined,
  id: string,
): string => {
  if (!id) return '-';
  if (!items) return id;
  return items.find((item) => item.id === id)?.name ?? id;
};

/** Serialize the editable subset so we can detect unsaved changes. */
const snapshot = (form: FormData, fns: MonitoringFunctionId[]): string =>
  JSON.stringify({ form, fns: [...fns].sort() });

/** "41000 J/mol (Q 4934 K)"; ", default" marks the engine's default Q. */
const activationEnergyText = (
  activationEnergy: number | string,
  q?: number | null,
  qIsDefault?: boolean,
): string =>
  `${activationEnergy} J/mol${q != null ? ` (Q ${q} K${qIsDefault ? ', default' : ''})` : ''}`;

/** The calibration and maturity inputs a saved pour holds, as form values
 *  (the server settles them; the form only shows them). */
const storedCalibrationFields = (
  pour: PourDetail,
): Pick<FormData, 'CalibrationID' | 'MaturityMethod' | 'ActivationEnergy'> => ({
  CalibrationID: pour.CalibrationID ?? '',
  MaturityMethod: pour.MaturityMethod ?? '',
  ActivationEnergy: pour.ActivationEnergy != null ? String(pour.ActivationEnergy) : '',
});

/** The pour's own batch plant as a form value: '' when it has none and shows
 *  its mix design's plant, as a pour created without one does. Older
 *  responses carry only the plant shown. */
const ownPlantId = (pour: PourDetail): string =>
  (pour.PourSupplierPlantID === undefined ? pour.SupplierPlantID : pour.PourSupplierPlantID) ?? '';

/** Values a select must offer beyond its option list, so it never shows an
 *  unknown value: the saved one (still there to pick again after None) and
 *  the form's own. */
const unlistedIds = (
  listed: (id: string) => boolean,
  ...ids: (string | null | undefined)[]
): string[] => Array.from(new Set(ids.filter((id): id is string => !!id && !listed(id))));

// ---------------------------------------------------------------------------
// Summary metric tile
// ---------------------------------------------------------------------------

const MetricCard: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 1.5, py: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25, minHeight: 24 }}>
      {value === null || value === undefined || value === '' ? '-' : value}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PourSetup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mode, pourId } = usePageMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  // ---- State ----
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [monitoringFunctions, setMonitoringFunctions] = useState<MonitoringFunctionId[]>([]);
  // The user's own function picks, apart from functions added as a
  // dependency. Kept here, not in the selector: the Function step unmounts
  // while another step shows, and a card clicked again after a visit to
  // another step must still take back only that pick.
  const [functionPicks, setFunctionPicks] = useState<MonitoringFunctionId[]>([]);
  const [pourDetail, setPourDetail] = useState<PourDetail | null>(null);
  // The loaded pour had a Mass Concrete Limit Group: its picker stays for
  // this visit after the group is cleared and saved, so it can be picked
  // again (on a maturity pour the group is what shows the picker).
  const [hadLimitGroup, setHadLimitGroup] = useState(false);

  // Editing needs pours.create (Client Admin / Project Admin / Super Admin);
  // viewer roles get the read-only page even on the /edit route.
  const permissionsKnown = Array.isArray(user?.permissions);
  const canEdit = hasPermission(user, 'pours.create');
  const canStop = hasPermission(user, 'pours.stop');
  // Inline "add new" buttons follow the matching create permission, so a
  // Project Admin editing a pour isn't offered a create that would 403.
  const canAddProject = hasPermission(user, 'projects.create');
  const canAddProduct = hasPermission(user, 'mix_designs.create');
  const canAddPlant = hasPermission(user, 'batch_plants.create');

  // Locked pours are read-only regardless of route mode — admins unlock
  // them from the Control Panel quick-view panel (UI Development doc 27.07).
  const pourLocked = Boolean(pourDetail?.Locked);
  const isView = mode === 'view' || pourLocked || !canEdit;
  const sectionMode: 'edit' | 'view' = isView ? 'view' : 'edit';
  // "Recalculate maturity" follows the saved pour (not unsaved function
  // edits): editors only, maturity pours only. On a locked pour it shows
  // disabled: an admin must unlock the pour before it can be recalculated.
  const showRecalcMaturity =
    canEdit &&
    stringToMonitoringFunctions(pourDetail?.MonitoringFunctions).includes('Concrete Maturity');
  const canRecalcMaturity = showRecalcMaturity && !pourLocked;
  // The mix design (and the maturity inputs and calibration that come with
  // it) locks at the pour's first temperature reading, as the server reports
  // it; until then a wrong mix can be corrected. The server refuses it too.
  const inputsLocked = Boolean(pourDetail?.MaturityInputsLocked);
  const [lookups, setLookups] = useState<PourLookups | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ---- Wizard step ----
  const [activeStep, setActiveStep] = useState(0);

  // Summary card starts collapsed so the wizard fits on screen.
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Baseline used for unsaved-changes detection.
  const [baselineKey, setBaselineKey] = useState('');
  const baselineData = useRef<{ form: FormData; funcs: MonitoringFunctionId[] }>({
    form: INITIAL_FORM_DATA,
    funcs: [],
  });

  // Stop-pour confirmation dialog + in-flight flag.
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  // null = unknown/loading; number = count of tank-bound locations on this
  // pour. Drives whether the dialog warns about tank reset.
  const [stopTankCount, setStopTankCount] = useState<number | null>(null);

  // "Recalculate maturity" confirmation dialog, in-flight flag and result.
  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Product info for the Product / Recipe field + Additional Information panel
  const [productDetail, setProductDetail] = useState<Product | null>(null);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);
  const [productCalibrations, setProductCalibrations] = useState<ProductCalibrationLink[]>([]);
  // A mix design picked on this page whose calibration links have loaded
  // (the form's CalibrationID is final for it), and the latest pick, so a
  // slower earlier load is dropped.
  const [calibrationsLoadedFor, setCalibrationsLoadedFor] = useState<string | null>(null);
  const productPickRef = useRef<string | null>(null);
  const [allProducts, setAllProducts] = useState<ProductListItem[]>([]);
  const [supplierPlants, setSupplierPlants] = useState<SupplierPlant[]>([]);

  // Counts drive the Stepper's completed ticks (locations / contacts).
  const [counts, setCounts] = useState<{ locations?: number; contacts?: number }>({});
  // Whether a saved location has Peak Temperature or differential pairs:
  // evidence that a maturity pour uses its mass-concrete controls.
  const [locationsUseMassConcrete, setLocationsUseMassConcrete] = useState(false);

  // A viewer who lands on /pours/:id/edit (bookmark, pasted link) is moved
  // to the read-only route so the URL matches what they can do.
  useEffect(() => {
    if (mode === 'edit' && permissionsKnown && !canEdit && pourId) {
      navigate(`/pours/${pourId}`, { replace: true });
    }
  }, [mode, permissionsKnown, canEdit, pourId, navigate]);

  // Pour-type access (company licensing ∩ per-user grant), same source as
  // PourCreate. null = unknown → nothing locked client-side; the backend
  // re-checks newly added functions on save either way.
  const [allowedFunctions, setAllowedFunctions] = useState<MonitoringFunctionId[] | null>(null);
  useEffect(() => {
    authAPI
      .getCurrentUser()
      .then((me) => {
        if (me.allowed_monitoring_functions) {
          setAllowedFunctions(me.allowed_monitoring_functions as MonitoringFunctionId[]);
        }
      })
      .catch(() => {});
  }, []);

  // ---- Keep Functionality (1/2/3) in sync with the multi-select cards ----
  useEffect(() => {
    if (monitoringFunctions.length === 0) return;
    const derived = deriveFunctionalityCode(monitoringFunctions);
    setFormData((prev) =>
      prev.Functionality === derived ? prev : { ...prev, Functionality: derived },
    );
  }, [monitoringFunctions]);

  const populateFormFromPour = useCallback((pour: PourDetail) => {
    const form: FormData = {
      PourCode: pour.PourCode ?? '',
      PourStatus: pour.PourStatus ?? '',
      PourName: pour.PourName ?? '',
      Functionality: pour.Functionality ?? '1',
      ProjectID: pour.ProjectID ?? '',
      Location: pour.Location ?? '',
      ProjectRef: pour.ProjectRef ?? '',
      PourVolume: pour.PourVolume != null ? String(pour.PourVolume) : '',
      Levels: pour.Levels ?? '',
      ProductID: pour.ProductID ?? '',
      SupplierPlantID: ownPlantId(pour),
      TargetGroupID: pour.TargetGroupID ?? '',
      LimitGroupID: pour.LimitGroupID ?? '',
      EvaLimitGroupID: pour.EvaLimitGroupID ?? '',
      WeatherStationID: pour.WeatherStationID ?? '',
      ...storedCalibrationFields(pour),
      StopType: pour.StopType ?? '',
      XHours: pour.XHours != null ? String(pour.XHours) : '',
      PreSetStopTime: pour.PreSetStopTime ?? '',
      Started: pour.Started ?? '',
      Finished: pour.Finished ?? '',
    };
    const funcs = stringToMonitoringFunctions(pour.MonitoringFunctions);
    setFormData(form);
    setMonitoringFunctions(funcs);
    // The stored selection counts as the user's own picks (which were
    // dependencies is not recorded).
    setFunctionPicks(funcs);
    setHadLimitGroup(!!pour.LimitGroupID);
    // Reset the dirty baseline to what the server just gave us.
    baselineData.current = { form, funcs };
    setBaselineKey(snapshot(form, funcs));

    if (pour.ProductID) {
      const productId = pour.ProductID;
      productsService
        .getProduct(productId)
        .then((prod) => {
          setProductDetail(prod);
          return Promise.all([
            productsService.listTargetGroups(productId),
            productsService.listProductCalibrations(productId),
          ]);
        })
        .then(([groups, calibrations]) => {
          setTargetGroups(groups);
          setProductCalibrations(calibrations);
        })
        .catch(() => { /* product may have been deleted */ });
    }
  }, []);

  // ---- Load lookups, pour, products and batch plants ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      // The pour itself is the only hard dependency. Lookups (dropdown
      // options) failing must not blank the page: fields fall back to the
      // names carried on the pour detail.
      const [pourResult, lookupsResult] = await Promise.allSettled([
        pourId ? poursAPI.getPourDetail(pourId) : Promise.resolve(null),
        poursAPI.getLookups(pourId),
      ]);
      if (cancelled) return;
      if (lookupsResult.status === 'fulfilled') setLookups(lookupsResult.value);
      if (pourResult.status === 'rejected') {
        const err: any = pourResult.reason;
        setError(apiErrorText(err, 'Failed to load data'));
      } else if (pourResult.value) {
        const pour = pourResult.value;
        setPourDetail(pour);
        populateFormFromPour(pour);
        // Products + plants power the same Autocomplete pickers as
        // PourCreate. They come from the POUR's company (a superadmin may be
        // looking at another client's pour); failures just leave the
        // dropdowns to the fallback options built from the pour detail.
        const companyId = pour.CompanyID || undefined;
        productsService.listProducts(companyId, true)
          .then((res) => { if (!cancelled) setAllProducts(Array.isArray(res) ? res : []); })
          .catch(() => {});
        productsService.listSupplierPlants(companyId, true)
          .then((res) => { if (!cancelled) setSupplierPlants(Array.isArray(res) ? res : []); })
          .catch(() => {});
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [pourId, populateFormFromPour]);

  // ---- Step-completion counts ----
  // The collection sections save immediately and only mount when visited, so
  // counts are fetched here and refreshed whenever the user switches steps.
  // The saved status and input lock come too: adding the first location puts
  // the pour In Progress, and its first reading locks the mix design; the
  // page should show both.
  const refreshCounts = useCallback(async () => {
    if (!pourId) return;
    const [locations, contacts, saved] = await Promise.all([
      poursAPI.getPourLocations2(pourId).catch(() => undefined),
      poursAPI.getPourContacts(pourId).then((r) => r.length).catch(() => undefined),
      poursAPI.getPourDetail(pourId).catch(() => null),
    ]);
    setCounts({ locations: locations?.length, contacts });
    if (locations) {
      setLocationsUseMassConcrete(
        locations.some((l) => !!l.has_temp_limit || (l.temp_diff_location_ids || []).length > 0),
      );
    }
    if (!saved) return;
    const status = saved.PourStatus ?? '';
    const inputsLock = Boolean(saved.MaturityInputsLocked);
    // A timed stop locks the pour while the page is open: pick that up too.
    const locked = Boolean(saved.Locked);
    setPourDetail((prev) =>
      prev &&
      (prev.PourStatus !== status ||
        Boolean(prev.MaturityInputsLocked) !== inputsLock ||
        Boolean(prev.Locked) !== locked)
        ? { ...prev, PourStatus: status, MaturityInputsLocked: inputsLock, Locked: locked }
        : prev,
    );
    // The weather station follows the pour's station location, set by the
    // server when one is added or removed on the Hardware step.
    const station = saved.WeatherStationID ?? '';
    setFormData((prev) =>
      prev.PourStatus === status && prev.WeatherStationID === station
        ? prev
        : { ...prev, PourStatus: status, WeatherStationID: station },
    );
    // Neither is a user edit: move the baseline with them.
    const base = baselineData.current;
    if (base.form.PourStatus !== status || base.form.WeatherStationID !== station) {
      baselineData.current = {
        ...base, form: { ...base.form, PourStatus: status, WeatherStationID: station },
      };
      setBaselineKey(snapshot(baselineData.current.form, base.funcs));
    }
  }, [pourId]);

  useEffect(() => {
    if (!loading) refreshCounts();
  }, [loading, activeStep, refreshCounts]);

  // ---- Dirty tracking ----
  const isDirty = !isView && baselineKey !== '' && snapshot(formData, monitoringFunctions) !== baselineKey;

  // Warn on full-page unload while there are unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Confirm before navigating away with unsaved changes.
  const guardedNavigate = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      if (isDirty && !window.confirm(t('pourSetup.unsavedLeaveConfirm', 'You have unsaved changes. Leave without saving?'))) {
        return;
      }
      navigate(to, options);
    },
    [isDirty, navigate, t],
  );

  // Revert staged pour-level edits back to the last-saved values.
  const handleDiscard = useCallback(() => {
    if (!window.confirm(t('pourSetup.discardConfirm', 'Discard your unsaved changes?'))) return;
    setFormData(baselineData.current.form);
    setMonitoringFunctions(baselineData.current.funcs);
    setFunctionPicks(baselineData.current.funcs);
    setFieldErrors({});
  }, [t]);

  // ---- Change handlers ----
  const handleChange = (field: keyof FormData) => (e: any) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSelectChange = (field: keyof FormData) => (e: any) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  // Resolve the selected project's IANA timezone so the datetime pickers
  // read/write in the project's local TZ instead of the browser's.
  // Falls back to the pour's own project timezone (on the detail) when the
  // project isn't in the lookups, so times never silently render in UTC.
  const projectTimezoneIana = useMemo(
    () =>
      lookups?.projects.find((p) => p.id === formData.ProjectID)?.timezone_iana ??
      (formData.ProjectID === pourDetail?.ProjectID ? pourDetail?.TimeZoneIANA : undefined),
    [lookups, formData.ProjectID, pourDetail],
  );

  /** UTC (stored) → 'YYYY-MM-DDTHH:mm' wall clock for a DateTimePicker. */
  const pickerValue = (utc: string): Dayjs | null => {
    if (!utc) return null;
    const local = utcToLocalInput(utc, projectTimezoneIana);
    return local ? dayjs(local) : null;
  };

  const handlePickerChange = (field: keyof FormData) => (val: Dayjs | null) =>
    setFormData((prev) => ({
      ...prev,
      [field]: val ? localInputToUtc(val.format('YYYY-MM-DDTHH:mm'), projectTimezoneIana) : '',
    }));

  // Times are shown in the project's (site's) timezone, labelled with it.
  const fmtDateTime = (value: string | null | undefined): string =>
    value
      ? `${formatInTimeZone(value, projectTimezoneIana)} ${timeZoneLabel(value, projectTimezoneIana)}`
      : '-';
  /** Picker label with the zone the time is entered in, e.g. "Pour Start Time (AEST)". */
  const tzLabelled = (label: string, value: string | null | undefined): string =>
    `${label} (${timeZoneLabel(value, projectTimezoneIana)})`;
  /** The zone those times are in, as the create wizard's review names it:
   *  "Australia/Brisbane (AEST)". Not the detail's short TimeZone, which the
   *  server makes up from the Windows name ("EAST" for Brisbane). */
  const zoneText = projectTimezoneIana
    ? `${projectTimezoneIana} (${timeZoneLabel(formData.Started, projectTimezoneIana)})`
    : t('pourSetup.noTimezone', 'UTC (the project has no timezone)');

  // ---- Product selection (mirrors PourCreate.handleProductSelect) ----
  const productOptions = useMemo(() => {
    const options = collapseProductRevisions(allProducts, formatProductOption);
    // Keep the pour's current product resolvable even when it is an older
    // revision or no longer in the active list.
    if (
      formData.ProductID &&
      !options.some((p) => p.product_id === formData.ProductID) &&
      productDetail?.product_id === formData.ProductID
    ) {
      options.push({
        product_id: productDetail.product_id,
        company_id: productDetail.company_id,
        code: productDetail.code,
        description: productDetail.description,
        supplier_plant_name: productDetail.supplier_plant_name,
        strength_grade: productDetail.strength_grade,
      });
    } else if (
      formData.ProductID &&
      !options.some((p) => p.product_id === formData.ProductID) &&
      formData.ProductID === pourDetail?.ProductID &&
      pourDetail?.ProductCode
    ) {
      // Product details not readable (e.g. role without mix-design access):
      // still show the pour's own mix from the names on the pour detail.
      options.push({
        product_id: pourDetail.ProductID,
        company_id: pourDetail.CompanyID ?? '',
        code: pourDetail.ProductCode,
        description: pourDetail.ProductDescription ?? '',
        supplier_plant_name: pourDetail.SupplierPlant,
        strength_grade: pourDetail.ProductStrengthGrade,
      });
    }
    return options;
  }, [allProducts, formData.ProductID, productDetail, pourDetail]);

  // Same idea for project and batch plant: the pour's saved selection stays
  // resolvable even when it isn't in the (company / active / permission
  // filtered) option lists.
  const projectOptions = useMemo(() => {
    const list = lookups?.projects ?? [];
    if (
      formData.ProjectID &&
      formData.ProjectID === pourDetail?.ProjectID &&
      pourDetail?.ProjectName &&
      !list.some((p) => p.id === formData.ProjectID)
    ) {
      return [...list, { id: pourDetail.ProjectID, name: pourDetail.ProjectName }];
    }
    return list;
  }, [lookups, formData.ProjectID, pourDetail]);

  // The pour's own saved plant, not the form's: after the field is cleared
  // it can still be picked again.
  const savedPlantId = pourDetail ? ownPlantId(pourDetail) : '';
  const plantOptions = useMemo(() => {
    if (
      savedPlantId &&
      pourDetail?.SupplierPlant &&
      !supplierPlants.some((p) => p.supplier_plant_id === savedPlantId)
    ) {
      return [
        ...supplierPlants,
        { supplier_plant_id: savedPlantId, name: pourDetail.SupplierPlant } as SupplierPlant,
      ];
    }
    return supplierPlants;
  }, [supplierPlants, savedPlantId, pourDetail]);

  const selectedProduct =
    productOptions.find((p) => p.product_id === formData.ProductID) ?? null;

  // Strength grades are in the company's unit (StrengthFormat): the mix
  // design's (else the pour's) company, and the user's company for a new mix
  // design. No unit is shown until it is known.
  const productCompanyId = selectedProduct?.company_id || pourDetail?.CompanyID;
  const [productStrengthUnit, ownStrengthUnit] = useCompanyStrengthUnits(
    productCompanyId,
    user?.CompanyID,
  );
  // The pour's target strengths are in the pour company's unit, as on the
  // dashboard, the charts and the legacy edit page (read once when the mix
  // design belongs to the same company).
  const pourIsProductCompany = sameCompany(pourDetail?.CompanyID, productCompanyId);
  const pourOwnStrengthUnit = useCompanyStrengthUnit(
    pourIsProductCompany ? null : pourDetail?.CompanyID,
  );
  const pourStrengthUnit = pourIsProductCompany ? productStrengthUnit : pourOwnStrengthUnit;
  // A mix design picked on this page and not saved yet.
  const mixDesignUnsaved = !!formData.ProductID && formData.ProductID !== (pourDetail?.ProductID ?? '');
  // The mix design's own plant: what a pour with no plant of its own shows.
  // The pour detail names the saved mix design's (a pour with none of its own
  // shows it); a new pick's comes with its details.
  const pickedProductDetail = productDetail?.product_id === formData.ProductID ? productDetail : null;
  const newMixPlantName = pickedProductDetail
    ? pickedProductDetail.supplier_plant_name
    : selectedProduct?.supplier_plant_name;
  const savedMixPlantName =
    pourDetail?.ProductSupplierPlant ||
    (!savedPlantId ? pourDetail?.SupplierPlant : undefined) ||
    pickedProductDetail?.supplier_plant_name;
  const mixPlantName = !formData.ProductID
    ? undefined
    : mixDesignUnsaved ? newMixPlantName : savedMixPlantName;
  // The plant the pour shows once saved: the picker's, else the mix design's.
  const plantShown = formData.SupplierPlantID
    ? plantOptions.find((p) => p.supplier_plant_id === formData.SupplierPlantID)?.name ||
      (formData.SupplierPlantID === pickedProductDetail?.supplier_plant_id
        ? pickedProductDetail?.supplier_plant_name
        : undefined)
    : mixPlantName;
  // The mix design or calibration differs from the saved pour's.
  const mixChanged =
    (formData.ProductID || '') !== (pourDetail?.ProductID || '') ||
    (formData.CalibrationID || '') !== (pourDetail?.CalibrationID || '');

  // ASTM C1074 9.2. A new mix design or calibration (possible before the
  // first reading) is checked here as the server checks it on save;
  // otherwise the saved pour's check from the detail response is shown, and
  // settled when the save makes the pour compute maturity (settleSaved).
  const checkPending = !isView && !inputsLocked && mixChanged;
  const pendingCalibrationsLoaded =
    !!formData.ProductID && calibrationsLoadedFor === formData.ProductID;
  // The rule holds the save only for a pour that computes maturity (Concrete
  // Maturity, as the functions stand on this page); for any other pour the
  // check is information. A refusal from the server counts whatever the page
  // thought, unless it refused a maturity pour that no longer computes
  // maturity.
  const computesMaturity = pourComputesMaturity(
    monitoringFunctionsToString(monitoringFunctions),
    formData.Functionality,
  );
  // The server picks the calibration (the mix design's active, finished one
  // with a usable curve, the newest of several), so it is asked with none.
  const pendingCheck = useCalibrationCheck(
    formData.ProductID,
    null,
    checkPending && pendingCalibrationsLoaded,
    computesMaturity,
  );
  // The calibration the pour gets is the one the check names. A refusal from
  // a save names no new pick, so the form keeps its calibration then.
  const pendingCheckResult = pendingCheck.check;
  const pendingCheckFromSave = pendingCheck.fromSave;
  useEffect(() => {
    if (!pendingCheckResult || pendingCheckFromSave) return;
    const calibrationId = pendingCheckResult.calibration_id ?? '';
    setFormData((prev) =>
      prev.CalibrationID === calibrationId ? prev : { ...prev, CalibrationID: calibrationId },
    );
  }, [pendingCheckResult, pendingCheckFromSave]);
  // A new mix design (with or without a maturity input group) gives the pour
  // that group's inputs, or else the Arrhenius defaults, which is what the
  // check previews: its result is shown and holds the save. Only a new
  // calibration on the saved mix design, when that mix has no group, leaves
  // the pour on its own stored inputs, which the check cannot see (it
  // assumes the defaults). Then only the save checks them, and its refusal
  // (422) is shown instead.
  const checkedOnSave =
    checkPending &&
    pendingCalibrationsLoaded &&
    !mixDesignUnsaved &&
    !productDetail?.maturity_method &&
    !pendingCheck.fromSave;
  const basisEnforced = computesMaturity || pendingCheck.refusalHolds;
  // Whether the saved pour computes maturity, judged as the server judges the
  // stored row: its recorded functions, else its Functionality code alone
  // (functions the detail inferred for a legacy pour, e.g. Temperature Match
  // Curing from a tank, are not on the row).
  const savedComputesMaturity = pourComputesMaturity(
    pourDetail?.MonitoringFunctionsInferred ? null : pourDetail?.MonitoringFunctions,
    pourDetail?.Functionality,
  );
  // A save that makes the pour compute maturity (Concrete Maturity added,
  // same mix design, before or after the first reading) is checked by the
  // server against the pour's own calibration and inputs, which is the saved
  // pour's check.
  const becomesMaturity = !isView && !checkPending && computesMaturity && !savedComputesMaturity;
  // The saved pour's own calibration is settled here like a new pick's when
  // the save makes it compute maturity, or when the server refused a save of
  // it anyway (its refusal then takes the place of the saved check).
  const settleSaved = !isView && !checkPending && (becomesMaturity || pendingCheck.refusalHolds);
  const settleSavedCheck = settleSaved
    ? (pendingCheck.fromSave ? pendingCheck.check : pourDetail?.calibration_check ?? null)
    : null;
  // Why saving must wait (mismatch, or differences not yet confirmed).
  const basisBlock = settleSaved
    ? calibrationCheckBlock(settleSavedCheck, pendingCheck.acknowledged)
    : checkPending && !checkedOnSave && basisEnforced
      ? calibrationCheckBlock(pendingCheck.check, pendingCheck.acknowledged)
      : null;
  // Why the new pick gets no calibration: no active, finished link (the
  // server has none either), or a link whose curve the server cannot use.
  const noCalibration = pendingCalibrationsLoaded
    ? noCalibrationText(productCalibrations, pendingCheck.check)
    : null;

  const handleProductSelect = async (product: ProductListItem | null) => {
    if (inputsLocked) return;
    productPickRef.current = product?.product_id ?? null;
    // The saved mix design picked again (after another one): the pour's own
    // calibration and maturity inputs come back, as a save leaves them as
    // they are, so there is nothing new to check. Another mix design's
    // calibration is the server's pick, named by the check.
    const savedMix = !!product && !!pourDetail && product.product_id === (pourDetail.ProductID ?? '');
    setCalibrationsLoadedFor(null);
    setProductDetail(null);
    setProductCalibrations([]);
    setTargetGroups([]);
    // The saved mix design also keeps the pour's own target and limit groups
    // and batch plant (never its defaults): a save then leaves them as they
    // are, even if its details fail to load, rather than clearing a group
    // shown empty or writing the mix design's plant onto a pour without one.
    setFormData((prev) => ({
      ...prev,
      ProductID: product?.product_id ?? '',
      TargetGroupID: savedMix && pourDetail ? pourDetail.TargetGroupID ?? '' : '',
      LimitGroupID: savedMix && pourDetail ? pourDetail.LimitGroupID ?? '' : '',
      EvaLimitGroupID: savedMix && pourDetail ? pourDetail.EvaLimitGroupID ?? '' : '',
      CalibrationID: '',
      ...(savedMix && pourDetail
        ? { ...storedCalibrationFields(pourDetail), SupplierPlantID: ownPlantId(pourDetail) }
        : {}),
    }));

    if (!product) return;

    try {
      const [detail, calibrations, groups] = await Promise.all([
        productsService.getProduct(product.product_id),
        productsService.listProductCalibrations(product.product_id),
        productsService.listTargetGroups(product.product_id).catch(() => [] as TargetGroup[]),
      ]);
      // Another mix design was picked while this one loaded.
      if (productPickRef.current !== product.product_id) return;
      setProductDetail(detail);
      setProductCalibrations(calibrations);
      setTargetGroups(groups);
      // A new mix design's CalibrationID stays empty until the check names
      // the server's pick.
      setCalibrationsLoadedFor(product.product_id);
      setFormData((prev) => ({
        ...prev,
        // Default the batch plant to the product's linked plant; keep the
        // user's own pick when the product doesn't specify one. The saved mix
        // design keeps the pour's own (set above).
        SupplierPlantID: savedMix
          ? prev.SupplierPlantID
          : detail.supplier_plant_id || prev.SupplierPlantID,
        // Display only (never sent): what a new mix design gives the pour on
        // save, its maturity group's inputs, or without a group the Arrhenius
        // defaults ('' activation energy: the default). The saved mix design
        // keeps the pour's own.
        ...(savedMix
          ? {}
          : {
              MaturityMethod: detail.maturity_method || DEFAULT_MATURITY_METHOD,
              ActivationEnergy:
                detail.maturity_method && detail.maturity_activation_energy != null
                  ? String(detail.maturity_activation_energy)
                  : '',
            }),
        // Another mix design's default group only while it still exists: one
        // deleted since stays named on the mix design but has no targets.
        TargetGroupID: savedMix
          ? pourDetail?.TargetGroupID ?? ''
          : detail.target_group_id && groups.some((g) => g.target_group_id === detail.target_group_id)
            ? detail.target_group_id
            : '',
        LimitGroupID: savedMix
          ? pourDetail?.LimitGroupID ?? ''
          : detail.mass_concrete_default_id ?? '',
        EvaLimitGroupID: savedMix
          ? pourDetail?.EvaLimitGroupID ?? ''
          : detail.evaporation_default_id ?? '',
      }));
    } catch (err) {
      console.error('Failed to load product details:', err);
    }
  };

  // ---- Stop pour ----
  const openStopDialog = useCallback(async () => {
    if (!pourId) return;
    setStopConfirmOpen(true);
    setStopTankCount(null);
    try {
      const locations = await poursAPI.getPourLocations2(pourId);
      const count = locations.filter(
        (loc) => loc.TankID != null && loc.TankID !== ''
      ).length;
      setStopTankCount(count);
    } catch {
      setStopTankCount(0);
    }
  }, [pourId]);

  const handleStopConfirm = async () => {
    if (!pourId) return;
    setStopping(true);
    setError(null);
    try {
      const result = await poursAPI.stopPour(pourId);
      setStopConfirmOpen(false);
      setStopTankCount(null);
      if (result.already_stopped) {
        setSuccess(t('pourSetup.alreadyStopped', 'Pour was already stopped'));
      } else {
        const tankNote = result.tanks_notified
          ? t(
              'pourSetup.stopSuccessWithTanks',
              { defaultValue: 'Pour stopped. {{count}} tank(s) reset.', count: result.tanks_notified },
            )
          : t('pourSetup.stopSuccess', 'Pour stopped.');
        setSuccess(String(tankNote));
      }
      const refreshed = await poursAPI.getPourDetail(pourId);
      // Stopping locks the pour on the server: the page turns read-only and
      // Recalculate maturity shows disabled with the unlock hint.
      setPourDetail(refreshed);
      populateFormFromPour(refreshed);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to stop pour'));
    } finally {
      setStopping(false);
    }
  };

  // ---- Recalculate maturity (every location, from recorded temperatures) ----
  const handleRecalcConfirm = async () => {
    if (!pourId) return;
    setRecalculating(true);
    try {
      const result = await poursAPI.recalculateMaturity(pourId);
      const count = result.locations_recalculated;
      setRecalcResult({
        ok: true,
        text: count > 0
          ? String(t('pourSetup.recalcDone', {
              defaultValue: 'Maturity recalculated for {{count}} location(s).',
              count,
            }))
          : t('pourSetup.recalcNothing', 'No recorded temperatures to recalculate yet.'),
      });
    } catch (err: any) {
      setRecalcResult({ ok: false, text: apiErrorText(err, 'Failed to recalculate maturity') });
    } finally {
      setRecalcConfirmOpen(false);
      setRecalculating(false);
    }
  };

  // ---- Stop settings ----
  // Checked as the server checks them: once StopType changes, or a setting
  // the chosen stop uses (XHours for the day and final-target stops,
  // PreSetStopTime for the set-time stop), so a saved legacy stop is
  // otherwise left as it is. On any other stop PreSetStopTime is the
  // Evaporation Calculation Stop Time, which never counts as a stop change.
  // The stop after the final targets needs Concrete Maturity, so it is also
  // checked when the pour stops computing maturity. Maturity changes nothing
  // for the other stops, and a legacy pour whose inferred functions differ
  // from its stored code must stay saveable.
  const baseForm = baselineData.current.form;
  const stopFieldsUnchanged =
    formData.StopType === baseForm.StopType &&
    (formData.XHours === baseForm.XHours || xHoursUnit(formData.StopType) === null) &&
    (formData.PreSetStopTime === baseForm.PreSetStopTime || formData.StopType !== STOP_AT_SET_TIME);
  const losesFinalTargets =
    formData.StopType === STOP_AFTER_FINAL_TARGETS && savedComputesMaturity && !computesMaturity;
  const stopUnchanged = stopFieldsUnchanged && !losesFinalTargets;
  const stopErrors: Record<string, string> =
    isView || stopUnchanged
      ? {}
      : stopSettingsErrors({
          stopType: formData.StopType,
          xHours: formData.XHours,
          presetUtc: formData.PreSetStopTime,
          computesMaturity,
          // A new or changed set time must still be ahead.
          presetMustBeFuture:
            formData.StopType === STOP_AT_SET_TIME &&
            (baseForm.StopType !== STOP_AT_SET_TIME ||
              formData.PreSetStopTime !== baseForm.PreSetStopTime),
          startUtc: formData.Started || undefined,
          allowLegacyStopType: true,
        });

  // ---- Save (unified save of pour-level fields) ----
  const handleSave = async (): Promise<boolean> => {
    if (!pourId || isView) return true;
    // ASTM C1074 9.2: the server refuses these too; say why before sending.
    if (basisBlock) {
      setError(basisBlock);
      return false;
    }
    // Saved from another step too: say where the stop settings are.
    const stopProblems = Object.values(stopErrors);
    if (stopProblems.length > 0) {
      setFieldErrors((p) => ({ ...p, ...stopErrors }));
      setError(`Check Stop Monitoring on Pour Details: ${stopProblems.join(' ')}`);
      return false;
    }
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      // Maturity inputs (method, datum, activation energy, specified
      // temperature) are never sent: for a new mix design the server copies
      // them from its maturity group, or without one resets them to the
      // defaults. Once the first reading is recorded the mix
      // design and calibration are locked, so they are left out as well,
      // unless they were changed before the lock and not saved (mixChanged):
      // then they are sent and the server's refusal is shown, rather than
      // the change being dropped without a word.
      const sendMix = !inputsLocked || mixChanged;
      // A corrected mix design that has no calibration or target group must
      // clear the old mix's ones (null), or its curve and targets would stay.
      const productChanged = (formData.ProductID || '') !== (pourDetail?.ProductID || '');
      const clearIfEmpty = (value: string) =>
        value || (sendMix && productChanged ? null : undefined);
      // An optional field the user emptied, that had a value when the pour
      // was loaded or last saved, is cleared (null): the server then stores
      // no value, as create does for a field left empty, and a cleared group
      // takes what it copied onto the pour with it, as a change of group
      // does. Only while its control shows; a field empty all along is not
      // sent.
      const valueOrClear = (field: keyof FormData, shown = true) =>
        formData[field].trim()
          ? formData[field]
          : shown && baseForm[field].trim() ? null : undefined;
      const payload: PourUpdate = {
        PourName: formData.PourName.trim(),
        Functionality: formData.Functionality || undefined,
        MonitoringFunctions: monitoringFunctions.length > 0
          ? monitoringFunctionsToString(monitoringFunctions)
          : undefined,
        ProjectID: formData.ProjectID || undefined,
        ProductID: sendMix ? formData.ProductID || undefined : undefined,
        // Cleared, the pour shows its mix design's plant again (if it has
        // one), as a pour created without a plant does.
        SupplierPlantID: valueOrClear('SupplierPlantID', showProduct),
        StopType: formData.StopType || undefined,
        // No field on this page (nor in the wizard): re-sent as loaded.
        Location: formData.Location || undefined,
        ProjectRef: valueOrClear('ProjectRef'),
        PourVolume: formData.PourVolume ? Number(formData.PourVolume) : undefined,
        Levels: valueOrClear('Levels'),
        // None clears the pour's group, as a corrected mix design without
        // one does: the server removes the pour's targets, and refuses it
        // for a pour that stops after its final targets.
        TargetGroupID:
          formData.TargetGroupID ||
          (sendMix && productChanged ? null : valueOrClear('TargetGroupID', showMaturityTarget)),
        // Only while its control shows: a new mix design's mass-concrete
        // default is not applied to a pour whose Temperature Only is
        // automatic, as in the wizard. None clears the pour's group (null):
        // the server then stores no group, as create does, and removes the
        // group's limits copied onto the pour, as a change of group does.
        LimitGroupID: showMassLimit
          ? formData.LimitGroupID || (pourDetail?.LimitGroupID ? null : undefined)
          : undefined,
        EvaLimitGroupID: valueOrClear('EvaLimitGroupID', showEvapLimit),
        CalibrationID: sendMix ? clearIfEmpty(formData.CalibrationID) : undefined,
        // Only a pour the rule holds is asked to confirm a difference.
        acknowledge_basis_difference: settleSaved
          ? acknowledgeFlag(settleSavedCheck, pendingCheck.acknowledged)
          : checkPending && !checkedOnSave && basisEnforced
            ? acknowledgeFlag(pendingCheck.check, pendingCheck.acknowledged)
            : undefined,
        // No WeatherStationID: the server takes it from the pour's weather
        // station location (hardware), so a save never changes it.
        XHours: formData.XHours ? Number(formData.XHours) : undefined,
        PreSetStopTime: formData.PreSetStopTime || undefined,
        Started: formData.Started || undefined,
        // Information only (nothing runs on it), and optional on create.
        Finished: valueOrClear('Finished'),
      };
      const updated = await poursAPI.updatePour(pourId, payload);
      setPourDetail(updated);
      // The server settles the calibration and the maturity inputs (a new
      // mix design's group, or the defaults): the form takes them from the
      // saved pour, so the save is not seen as a change still to check.
      const settled = storedCalibrationFields(updated);
      setFormData((prev) => ({ ...prev, ...settled }));
      // Saved values become the new baseline → clears the dirty state.
      const savedForm = { ...formData, ...settled };
      baselineData.current = { form: savedForm, funcs: monitoringFunctions };
      setBaselineKey(snapshot(savedForm, monitoringFunctions));
      // The saved pour's check (in the reply) is the one shown from now on,
      // not an earlier refusal.
      pendingCheck.reset();
      setSuccess(t('pourSetup.savedSuccess', 'Pour saved successfully'));
      return true;
    } catch (err: any) {
      const text = apiErrorText(err, 'Failed to save pour');
      // A refusal on the calibration rules also shows in the check panel,
      // where it can be settled (confirm the difference, or check again),
      // even when the page expected none: the server's answer counts.
      if (err?.response?.status === 422) {
        pendingCheck.applySaveRefusal(text);
      }
      setError(text);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ---- Conditional visibility derived from the monitoring functions ----
  const fnSet = useMemo(() => new Set(monitoringFunctions), [monitoringFunctions]);
  const hasTemp = fnSet.has('Temperature Only');
  const hasMaturity = fnSet.has('Concrete Maturity');
  const hasTMC = fnSet.has('Temperature Match Curing');
  const hasEvap = fnSet.has('Calculate Concrete Evaporation Rate');

  const showProduct = hasTemp || hasMaturity || hasTMC || hasEvap;
  const showMaturityTarget = hasMaturity || hasTMC;
  // The wizard adds Temperature Only to every maturity pour as a dependency,
  // and the stored functions do not say whether it was also ticked. With
  // Concrete Maturity it brings its mass-concrete controls only when the
  // pour evidently uses them (a limit group, or a location with Peak
  // Temperature or differential pairs); otherwise it counts as automatic,
  // as in the wizard. A group cleared and saved on this visit keeps the
  // picker, so it can be picked again.
  const massConcreteUsed = hadLimitGroup || !!pourDetail?.LimitGroupID || locationsUseMassConcrete;
  const showMassLimit = hasTemp && (!hasMaturity || massConcreteUsed);
  const showEvapLimit = hasEvap;

  // ---- Step validation / completion (drives ticks + Next gating) ----
  const detailsComplete =
    !!formData.PourName.trim() && !!formData.ProjectID && (!showProduct || !!formData.ProductID) &&
    Object.keys(stopErrors).length === 0;

  const isStepComplete = (n: number): boolean => {
    switch (n) {
      case 0: return monitoringFunctions.length > 0;
      case 1: return detailsComplete;
      case 2: return (counts.locations ?? 0) > 0;
      case 3: return (counts.contacts ?? 0) > 0;
      default: return false;
    }
  };

  const validateStep = (step: number): boolean => {
    if (isView) return true;
    switch (step) {
      case 0:
        if (monitoringFunctions.length === 0) {
          setError('Please select at least one monitoring function.');
          return false;
        }
        return true;
      case 1: {
        const errs: Record<string, string> = { ...stopErrors };
        if (!formData.PourName.trim()) errs.pourName = 'Pour name is required.';
        if (!formData.ProjectID) errs.projectId = 'Project is required.';
        if (showProduct && !formData.ProductID) errs.product = 'Product / Mix Design is required.';
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
      }
      default:
        return true;
    }
  };

  // ---- Step navigation ----
  const goToStep = (target: number) => {
    if (target === activeStep) return;
    setError(null);
    setActiveStep(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const handleNext = async () => {
    setError(null);
    if (!validateStep(activeStep)) return;
    // Editing saves as you go: SAVE / NEXT commits any staged pour-level
    // changes before moving on (the collection sections already saved).
    if (!isView && isDirty) {
      const ok = await handleSave();
      if (!ok) return;
    }
    if (activeStep === STEPS.length - 1) {
      if (!isView && pourId) navigate(`/pours/${pourId}/dashboard`);
    } else {
      setActiveStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ---- Quick-add Project dialog (mirrors PourCreate) ----
  const [addProjectOpen, setAddProjectOpen] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ name: '', client: '', address: '', timezone: '' });
  const [newProjectLatLon, setNewProjectLatLon] = useState<{ lat?: string; lon?: string }>({});
  const [addProjectSaving, setAddProjectSaving] = useState(false);

  // Existing client names for the autocomplete; loaded when the dialog first opens
  const [clientOptions, setClientOptions] = useState<string[] | null>(null);
  useEffect(() => {
    if (addProjectOpen && clientOptions === null) {
      projectsService.getClients().then(setClientOptions).catch(() => setClientOptions([]));
    }
  }, [addProjectOpen, clientOptions]);

  const resetProjectDialog = () => {
    setNewProjectForm({ name: '', client: '', address: '', timezone: '' });
    setNewProjectLatLon({});
    setAddProjectOpen(false);
  };

  const handleAddProject = async () => {
    if (!newProjectForm.name.trim() || !user?.CompanyID) return;
    setAddProjectSaving(true);
    try {
      const created = await projectsService.createProject({
        project_name: newProjectForm.name.trim(),
        company_id: user.CompanyID,
        client: newProjectForm.client.trim() || undefined,
        address: newProjectForm.address.trim() || undefined,
        timezone: newProjectForm.timezone || undefined,
        latitude: newProjectLatLon.lat || undefined,
        longitude: newProjectLatLon.lon || undefined,
      });
      // The lookups carry each project's timezone (timezone_iana, which the
      // server works out from the project's zone): read them again so the
      // times picked for the new project are in its timezone, not UTC. The
      // entry is added by hand only when they cannot be read.
      const fresh = await poursAPI.getLookups(pourId).catch(() => null);
      setLookups((prev) => {
        const next = fresh ?? prev;
        if (!next) return next;
        return next.projects.some((p) => p.id === created.project_id)
          ? next
          : { ...next, projects: [...next.projects, { id: created.project_id, name: created.project_name }] };
      });
      setFormData((prev) => ({ ...prev, ProjectID: created.project_id }));
      resetProjectDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create project'));
    } finally {
      setAddProjectSaving(false);
    }
  };

  // ---- Quick-add Product dialog (mirrors PourCreate) ----
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    code: '', description: '', strength_grade: '', supplier_plant_id: '',
    mass_concrete_default_id: '', evaporation_default_id: '',
  });
  const [addProductSaving, setAddProductSaving] = useState(false);

  const resetProductDialog = () => {
    setNewProductForm({
      code: '', description: '', strength_grade: '', supplier_plant_id: '',
      mass_concrete_default_id: '', evaporation_default_id: '',
    });
    setAddProductOpen(false);
  };

  const handleAddProduct = async () => {
    if (!newProductForm.code.trim() || !user?.CompanyID) return;
    setAddProductSaving(true);
    try {
      const created = await productsService.createProduct({
        code: newProductForm.code.trim(),
        description: newProductForm.description.trim(),
        company_id: user.CompanyID,
        supplier_plant_id: newProductForm.supplier_plant_id || undefined,
        strength_grade: newProductForm.strength_grade ? parseInt(newProductForm.strength_grade) : undefined,
        mass_concrete_default_id: newProductForm.mass_concrete_default_id || undefined,
        evaporation_default_id: newProductForm.evaporation_default_id || undefined,
      });
      const plantName = supplierPlants.find((p) => p.supplier_plant_id === newProductForm.supplier_plant_id)?.name;
      const newItem: ProductListItem = {
        product_id: created.product_id,
        company_id: created.company_id,
        code: created.code,
        description: created.description,
        supplier_plant_name: plantName || created.supplier_plant_name,
        strength_grade: created.strength_grade,
      };
      setAllProducts((prev) => [...prev, newItem]);
      handleProductSelect(newItem);
      resetProductDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create product'));
    } finally {
      setAddProductSaving(false);
    }
  };

  // ---- Quick-add Batch Plant dialog (mirrors PourCreate) ----
  const [addPlantOpen, setAddPlantOpen] = useState(false);
  const [newPlantForm, setNewPlantForm] = useState({
    name: '', ready_mix_supplier: '', address: '', latitude: '', longitude: '',
  });
  const [addPlantSaving, setAddPlantSaving] = useState(false);

  const resetPlantDialog = () => {
    setNewPlantForm({ name: '', ready_mix_supplier: '', address: '', latitude: '', longitude: '' });
    setAddPlantOpen(false);
  };

  const handleAddPlant = async () => {
    if (!newPlantForm.name.trim() || !user?.CompanyID) return;
    setAddPlantSaving(true);
    try {
      const created = await productsService.createSupplierPlant({
        name: newPlantForm.name.trim(),
        company_id: user.CompanyID,
        ready_mix_supplier: newPlantForm.ready_mix_supplier.trim() || undefined,
        address: newPlantForm.address.trim() || undefined,
        latitude: newPlantForm.latitude || undefined,
        longitude: newPlantForm.longitude || undefined,
      });
      setSupplierPlants((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setFormData((prev) => ({ ...prev, SupplierPlantID: created.supplier_plant_id }));
      resetPlantDialog();
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to create batch plant'));
    } finally {
      setAddPlantSaving(false);
    }
  };

  // ---- Loading / not-found ----
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pourId || !pourDetail) {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="warning">{t('pourSetup.notFound', 'Pour not found.')}</Alert>
      </Box>
    );
  }

  // ---- Derived display values ----
  // Only the pour's own calibration: a pour without one has no strength, so
  // the mix design's active calibration is not shown in its place.
  const selectedCalibration = formData.CalibrationID
    ? productCalibrations.find((c) => c.calibration_id === formData.CalibrationID)
    : undefined;
  const calibrationDisplay = selectedCalibration
    ? [selectedCalibration.calibration_code, selectedCalibration.calibration_name]
        .filter(Boolean).join(' · ')
    : formData.CalibrationID && formData.CalibrationID === pourDetail.CalibrationID
      ? pourDetail.CalibrationName || undefined
      : (checkPending && formData.CalibrationID && pendingCheck.check?.calibration_name) || undefined;
  // The calibration check shown: the new pick's (unless only the save can
  // check it), the saved pour's one to settle (or the server's refusal in its
  // place), or the saved pour's.
  const shownCheck = checkPending
    ? (checkedOnSave ? null : pendingCheck.check)
    : settleSaved
      ? settleSavedCheck
      : pourDetail.calibration_check ?? null;
  // Shown in the maturity panel of a maturity pour, and wherever a check must
  // be settled; on any other pour a new pick whose basis differs shows too,
  // as information.
  const shownStatus = shownCheck?.status;
  const showCalibrationCheck =
    !!formData.ProductID &&
    (showMaturityTarget ||
      settleSaved ||
      (checkPending && (!!basisBlock || shownStatus === 'differs' || shownStatus === 'mismatch')));
  // The maturity inputs shown are what the pour computes with once saved:
  // the mix design's maturity input group (the backend copies a new one's
  // onto the pour), then the pour's own value (a new mix design without a
  // group: the Arrhenius defaults the save applies), then the default the
  // engine falls back to (the checked pour basis). Never the calibration's.
  // A newly picked mix design shows none until it has loaded.
  const maturityInputsKnown = !mixDesignUnsaved || pendingCalibrationsLoaded;
  const maturityMethodShown = maturityInputsKnown
    ? productDetail?.maturity_method || formData.MaturityMethod
    : null;
  const displayBasis = checkPending && !checkedOnSave
    ? pendingCheck.check?.pour_basis
    : pourDetail.calibration_check?.pour_basis;
  const defaultBasis = displayBasis && displayBasis.method !== 'Nurse Saul' ? displayBasis : null;
  const activationEnergyDisplay: string | null =
    maturityMethodShown == null || maturityMethodShown === 'Nurse Saul'
      ? null
      : productDetail?.maturity_activation_energy != null
        ? activationEnergyText(productDetail.maturity_activation_energy, productDetail.maturity_q)
        : formData.ActivationEnergy
          ? activationEnergyText(formData.ActivationEnergy)
          : defaultBasis?.activation_energy != null
            ? activationEnergyText(defaultBasis.activation_energy, defaultBasis.q_value, defaultBasis.q_is_default)
            : null;

  const projectName = lookupName(lookups?.projects, formData.ProjectID);

  // ---- Render Steps (layout mirrors PourCreate step-for-step) ----

  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        What Type of Pour will you be monitoring?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select one or multiple functions. Dependencies are selected automatically.
      </Typography>
      {pourDetail.MonitoringFunctionsInferred && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isView
            ? t(
                'pourSetup.functionsInferredView',
                'This pour was set up before monitoring functions were recorded, so the selection below is inferred from its settings (functionality, weather station, evaporation limits, tanks).',
              )
            : t(
                'pourSetup.functionsInferredEdit',
                'This pour was set up before monitoring functions were recorded, so the selection below is inferred from its settings. Check it and save to store it on the pour.',
              )}
        </Alert>
      )}
      <Box sx={isView ? { pointerEvents: 'none', opacity: 0.85 } : undefined}>
        <PourFunctionSelector
          value={monitoringFunctions}
          onChange={setMonitoringFunctions}
          picks={functionPicks}
          onPicksChange={setFunctionPicks}
          isAllowed={
            isView || allowedFunctions === null
              ? undefined
              : (fn) => allowedFunctions.includes(fn) || baselineData.current.funcs.includes(fn)
          }
        />
      </Box>
    </Box>
  );

  const renderStep1 = () => {
    return (
    <Box sx={{ fontSize: '0.875rem' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2.5 }}>
        Pour Details
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 3, md: 4 } }}>
        {/* ── Left column: form fields — same stack as PourCreate ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Pour Name"
            size="small"
            value={formData.PourName}
            onChange={(e) => {
              handleChange('PourName')(e);
              if (fieldErrors.pourName) setFieldErrors((p) => ({ ...p, pourName: '' }));
            }}
            fullWidth
            required
            disabled={isView}
            inputProps={{ maxLength: 50 }}
            error={!!fieldErrors.pourName}
            helperText={fieldErrors.pourName}
          />

          {/* Pour Start / Finish Time */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <DateTimePicker
              label={tzLabelled('Pour Start Time', formData.Started)}
              value={pickerValue(formData.Started)}
              onChange={handlePickerChange('Started')}
              minutesStep={30}
              ampm={false}
              disabled={isView}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  // The server moves the locations still waiting to start
                  // at the saved start time to the new one.
                  helperText: isView
                    ? undefined
                    : t('pourSetup.startMovesSensors', 'Sensors due to start at this time move with it.'),
                },
                actionBar: { sx: { display: 'none' } },
                popper: { sx: PICKER_POPPER_SX },
              }}
            />
            <DateTimePicker
              label={tzLabelled('Pour Finish Time', formData.Finished)}
              value={pickerValue(formData.Finished)}
              onChange={handlePickerChange('Finished')}
              minutesStep={30}
              ampm={false}
              disabled={isView}
              slotProps={{
                // Optional: it can be taken off again (a save clears it).
                field: { clearable: !isView },
                textField: { fullWidth: true, size: 'small' },
                actionBar: { sx: { display: 'none' } },
                popper: { sx: PICKER_POPPER_SX },
              }}
            />
          </Box>

          {/* Stop Monitoring: after X days, X hours after the final targets
              (maturity pours only; a legacy pour keeps showing its own), or
              at a set time */}
          <StopMonitoringFields
            stopType={formData.StopType}
            xHours={formData.XHours}
            disabled={isView}
            errors={{ stopType: fieldErrors.stopType, xHours: fieldErrors.xHours }}
            currentStopText={describeStop(
              baseForm.StopType,
              baseForm.XHours,
              baseForm.PreSetStopTime ? fmtDateTime(baseForm.PreSetStopTime) : '',
            )}
            onChange={(next) => {
              setFormData((prev) => ({ ...prev, StopType: next.stopType, XHours: next.xHours }));
              setFieldErrors((p) => ({ ...p, stopType: '', xHours: '' }));
            }}
          />

          {/* Project */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(p) => p.name || ''}
              value={projectOptions.find((p) => p.id === formData.ProjectID) || null}
              onChange={(_e, newValue) => {
                setFormData((prev) => ({ ...prev, ProjectID: newValue?.id || '' }));
                if (fieldErrors.projectId) setFieldErrors((p) => ({ ...p, projectId: '' }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No projects available"
              size="small"
              disabled={isView}
              slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  fullWidth
                  required={!isView}
                  error={!!fieldErrors.projectId}
                  helperText={fieldErrors.projectId}
                />
              )}
            />
            {!isView && canAddProject && (
              <IconButton sx={{ color: BRAND_GREEN }} title="Add new project" onClick={() => setAddProjectOpen(true)}>
                <AddCircleIcon />
              </IconButton>
            )}
          </Box>

          {/* Product / Recipe (conditional) */}
          {showProduct && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={productOptions}
                getOptionLabel={formatProductOption}
                value={selectedProduct}
                onChange={(_e, newValue) => {
                  handleProductSelect(newValue);
                  if (fieldErrors.product) setFieldErrors((p) => ({ ...p, product: '' }));
                }}
                isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                noOptionsText="No products available"
                size="small"
                disabled={isView || inputsLocked}
                slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product / Recipe"
                    fullWidth
                    required={!isView}
                    error={!!fieldErrors.product}
                    helperText={
                      fieldErrors.product ||
                      (inputsLocked && !isView
                        ? t('pourSetup.inputsLocked', 'Locked once the first reading is recorded')
                        : undefined)
                    }
                  />
                )}
              />
              {!isView && !inputsLocked && canAddProduct && (
                <IconButton sx={{ color: BRAND_GREEN }} title="Add new product" onClick={() => setAddProductOpen(true)}>
                  <AddCircleIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Batch plant (conditional) */}
          {showProduct && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={plantOptions}
                getOptionLabel={(p) => p.name}
                value={plantOptions.find((p) => p.supplier_plant_id === formData.SupplierPlantID) || null}
                onChange={(_e, newValue) =>
                  setFormData((prev) => ({ ...prev, SupplierPlantID: newValue?.supplier_plant_id || '' }))
                }
                isOptionEqualToValue={(option, value) => option.supplier_plant_id === value.supplier_plant_id}
                noOptionsText="No batch plants available"
                size="small"
                disabled={isView}
                slotProps={DROPDOWN_AUTOCOMPLETE_SLOT_PROPS}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier / Batch Plant"
                    fullWidth
                    // None of its own: the pour shows its mix design's plant,
                    // as one created without a plant does.
                    helperText={
                      !formData.SupplierPlantID && mixPlantName
                        ? `Not set for this pour. It shows the mix design's plant: ${mixPlantName}`
                        : undefined
                    }
                  />
                )}
              />
              {!isView && canAddPlant && (
                <IconButton sx={{ color: BRAND_GREEN }} title="Add new batch plant" onClick={() => setAddPlantOpen(true)}>
                  <AddCircleIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Limit groups (conditional) */}
          {showMaturityTarget && (
            <FormControl fullWidth size="small" disabled={isView}>
              <InputLabel>Maturity Target Group</InputLabel>
              <Select
                value={formData.TargetGroupID}
                label="Maturity Target Group"
                onChange={handleSelectChange('TargetGroupID')}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {/* The saved group only with the saved mix design: groups
                    belong to a mix design. */}
                {unlistedIds(
                  (id) => targetGroups.some((tg) => tg.target_group_id === id),
                  mixDesignUnsaved ? undefined : pourDetail.TargetGroupID,
                  formData.TargetGroupID,
                ).map((id) => (
                  <MenuItem key={id} value={id}>
                    {(id === pourDetail.TargetGroupID && pourDetail.TargetGroupName) || 'Current target group'}
                  </MenuItem>
                ))}
                {targetGroups.map((tg) => (
                  <MenuItem key={tg.target_group_id} value={tg.target_group_id}>
                    {tg.target_group_name || tg.target_group_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {showMassLimit && (
            <FormControl fullWidth size="small" disabled={isView}>
              <InputLabel>Mass Concrete Limit Group</InputLabel>
              <Select
                value={formData.LimitGroupID}
                label="Mass Concrete Limit Group"
                onChange={handleSelectChange('LimitGroupID')}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {unlistedIds(
                  (id) => (lookups?.mass_concrete_defaults || []).some((mc) => mc.id === id),
                  pourDetail.LimitGroupID,
                  formData.LimitGroupID,
                ).map((id) => (
                  <MenuItem key={id} value={id}>Current limit group</MenuItem>
                ))}
                {(lookups?.mass_concrete_defaults || []).map((mc) => (
                  <MenuItem key={mc.id} value={mc.id}>{mc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {showEvapLimit && (
            <FormControl fullWidth size="small" disabled={isView}>
              <InputLabel>Evaporation Limit Group</InputLabel>
              <Select
                value={formData.EvaLimitGroupID}
                label="Evaporation Limit Group"
                onChange={handleSelectChange('EvaLimitGroupID')}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {unlistedIds(
                  (id) => (lookups?.evaporation_defaults || []).some((ed) => ed.id === id),
                  pourDetail.EvaLimitGroupID,
                  formData.EvaLimitGroupID,
                ).map((id) => (
                  <MenuItem key={id} value={id}>
                    {(id === pourDetail.EvaLimitGroupID && pourDetail.EvaporationLimitName) ||
                      'Current evaporation group'}
                  </MenuItem>
                ))}
                {(lookups?.evaporation_defaults || []).map((ed) => (
                  <MenuItem key={ed.id} value={ed.id}>{ed.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Evaporation calc stop time (shares PreSetStopTime with the preset
              stop type; only shown separately when the pour calculates
              evaporation and the stop type isn't already preset-time). */}
          {hasEvap && formData.StopType !== '22' && (
            <DateTimePicker
              label={tzLabelled('Evaporation Calculation Stop Time', formData.PreSetStopTime)}
              value={pickerValue(formData.PreSetStopTime)}
              onChange={handlePickerChange('PreSetStopTime')}
              minutesStep={30}
              ampm={false}
              disabled={isView}
              slotProps={{
                textField: { fullWidth: true, size: 'small' },
                actionBar: { sx: { display: 'none' } },
                popper: { sx: PICKER_POPPER_SX },
              }}
            />
          )}

          <TextField
            label="Drawing / Reference Number"
            size="small"
            value={formData.ProjectRef}
            onChange={handleChange('ProjectRef')}
            fullWidth
            disabled={isView}
          />
          <TextField
            label="Additional Notes / Comments"
            size="small"
            value={formData.Levels}
            onChange={handleChange('Levels')}
            fullWidth
            multiline
            rows={3}
            disabled={isView}
          />
        </Box>

        {/* ── Right column: Attachments + Additional Info ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <PourAttachmentsSection pourId={pourId} mode={sectionMode} variant="dropzone" />

          {/* Additional Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Additional Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '110px 1fr', sm: '160px 1fr' }, columnGap: 1.5, rowGap: 0.75, fontSize: '0.875rem' }}>
              <Typography variant="body2" color="text.secondary">Pour Code</Typography>
              <Typography variant="body2">{formData.PourCode || '-'}</Typography>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Typography variant="body2">
                {formData.PourStatus ? statusChipLabel(formData.PourStatus) : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Setup By</Typography>
              <Typography variant="body2">{pourDetail.SetupBy || '-'}</Typography>
              <Typography variant="body2" color="text.secondary">Time Zone</Typography>
              <Typography variant="body2">{zoneText}</Typography>
              {selectedProduct && (
                <>
                  <Typography variant="body2" color="text.secondary">Product Code</Typography>
                  <Typography variant="body2">{selectedProduct.code}</Typography>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedProduct.description || '-'}</Typography>
                  {plantShown && (
                    <>
                      <Typography variant="body2" color="text.secondary">Supplier Plant</Typography>
                      <Typography variant="body2">{plantShown}</Typography>
                    </>
                  )}
                  {selectedProduct.strength_grade != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Strength Grade</Typography>
                      <Typography variant="body2">{valueWithUnit(selectedProduct.strength_grade, productStrengthUnit)}</Typography>
                    </>
                  )}
                  {showMaturityTarget && maturityMethodShown != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Maturity Method</Typography>
                      <Typography variant="body2">{describeMethod(maturityMethodShown)}</Typography>
                    </>
                  )}
                  {showMaturityTarget && productDetail?.maturity_group_id && (
                    <>
                      <Typography variant="body2" color="text.secondary">Maturity Input Group</Typography>
                      <Typography variant="body2">{productDetail.maturity_group_id}</Typography>
                    </>
                  )}
                  {showMaturityTarget && productDetail?.maturity_datum_temp != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Datum Temperature</Typography>
                      <Typography variant="body2">{productDetail.maturity_datum_temp} °C</Typography>
                    </>
                  )}
                  {showMaturityTarget && productDetail?.maturity_specified_temp != null && (
                    <>
                      <Typography variant="body2" color="text.secondary">Specified Temperature</Typography>
                      <Typography variant="body2">{productDetail.maturity_specified_temp} °C</Typography>
                    </>
                  )}
                  {showMaturityTarget && activationEnergyDisplay && (
                    <>
                      <Typography variant="body2" color="text.secondary">Activation Energy</Typography>
                      <Typography variant="body2">{activationEnergyDisplay}</Typography>
                    </>
                  )}
                  {showMaturityTarget && calibrationDisplay && (
                    <>
                      <Typography variant="body2" color="text.secondary">Calibration</Typography>
                      <Typography variant="body2">{calibrationDisplay}</Typography>
                    </>
                  )}
                </>
              )}
            </Box>
            {showCalibrationCheck && (
              <Box sx={{ mt: 1.5 }}>
                {checkPending ? (
                  <CalibrationCheckPanel
                    check={pendingCheck.check}
                    loading={pendingCheck.loading}
                    failed={pendingCheck.failed}
                    noCalibrationText={noCalibration}
                    checkedOnSave={checkedOnSave}
                    informational={!basisEnforced}
                    acknowledged={pendingCheck.acknowledged}
                    onAcknowledgeChange={pendingCheck.setAcknowledged}
                  />
                ) : settleSaved ? (
                  // The way out of a mismatch here is to remove Concrete
                  // Maturity; another mix design only before the first reading.
                  <CalibrationCheckPanel
                    check={shownCheck}
                    acknowledged={pendingCheck.acknowledged}
                    onAcknowledgeChange={pendingCheck.setAcknowledged}
                    mismatchAdvice={
                      inputsLocked
                        ? MISMATCH_ADVICE_ADDED_MATURITY_LOCKED_TEXT
                        : MISMATCH_ADVICE_ADDED_MATURITY_TEXT
                    }
                  />
                ) : (
                  <CalibrationCheckPanel check={shownCheck} />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
    );
  };

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Hardware / Sensors
      </Typography>
      {/* Empty lookups if they failed to load: rows still show (device codes
          come on each location), only the pickers are empty. */}
      <PourLocationsSection
        pourId={pourId}
        mode={sectionMode}
        lookups={lookups ?? EMPTY_LOOKUPS}
        pourContext={{
          WeatherStationID: formData.WeatherStationID,
          TargetGroupID: formData.TargetGroupID,
          CalibrationID: formData.CalibrationID,
          // The method the engine calculates with once saved: the pour's own,
          // or a newly picked mix design's group method (without a group the
          // Arrhenius default), as handleProductSelect sets it.
          MaturityMethod: formData.MaturityMethod,
          // The Ts the engine calculates with: the saved pour's (20 °C when
          // null). Only a newly picked, unsaved mix design names its group's
          // Ts, which the server copies onto the pour on save, or without
          // one the default Ts the check reports.
          SpecifiedTemp: mixDesignUnsaved
            ? productDetail?.maturity_specified_temp ?? pendingCheck.check?.pour_basis?.specified_temp
            : pourDetail?.SpecifiedTemp,
          Functionality: formData.Functionality,
          MonitoringFunctions: monitoringFunctionsToString(monitoringFunctions),
          PourStatus: formData.PourStatus,
        }}
        projectTimezoneIana={projectTimezoneIana}
        strengthUnit={pourStrengthUnit}
      />
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Notifications / Alerts
      </Typography>
      <PourContactsSection pourId={pourId} mode={sectionMode} lookups={lookups ?? EMPTY_LOOKUPS} />
    </Box>
  );

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <Box
      sx={{
        maxWidth: 1600,
        mx: 'auto',
        px: { xs: 0, md: 5 },
        pb: { xs: 12, md: 6 },
        display: 'flex',
        flexDirection: 'column',
        // Fill the viewport below the app chrome so the action bar can pin to
        // the bottom of the page even on short steps (mt: 'auto' below).
        minHeight: { md: 'calc(100vh - 170px)' },
      }}
    >
      {/* Locked banner — the pour is read-only until an admin unlocks it */}
      {pourLocked && (
        <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
          {t(
            'pourSetup.lockedBanner',
            'This pour is locked and read-only. An administrator can unlock it from the Control Panel to allow changes.',
          )}
        </Alert>
      )}

      {/* Combined header bar — navigation, pour identity, status chips and
          page actions in one row; clicking the row (outside the buttons)
          expands the pour summary details. Collapsed by default so the
          wizard fits on one screen. */}
      <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 2, borderRadius: 1.5 }}>
        <Box
          onClick={() => setSummaryOpen((open) => !open)}
          role="button"
          aria-expanded={summaryOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {/* Left: back + pour identity + status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Tooltip title={t('pourSetup.backToPours', 'Back to Pours')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  guardedNavigate('/pours');
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" component="h2" noWrap sx={{ fontWeight: 700 }}>
              {pourDetail.PourName || '-'}
            </Typography>
            {pourDetail.PourStatus && (
              <Chip
                label={statusChipLabel(pourDetail.PourStatus)}
                size="small"
                color={statusChipColor(pourDetail.PourStatus)}
              />
            )}
            {pourLocked && (
              <Chip
                label={t('pourSetup.locked', 'Locked')}
                size="small"
                icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              />
            )}
            <Chip
              label={isView ? t('pourSetup.viewing', 'Viewing') : t('pourSetup.editing', 'Editing')}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Right: page actions + expand toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {!isView && pourId && (
              <Button
                variant="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  guardedNavigate(`/pours/${pourId}/edit-legacy`);
                }}
                sx={{ textTransform: 'none', color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {t('pourSetup.compareWithLegacy', 'Previous editor')}
              </Button>
            )}

            {pourId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DashboardIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  guardedNavigate(`/pours/${pourId}/dashboard`);
                }}
                disabled={saving || stopping}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {t('pourSetup.dashboard', 'Dashboard')}
              </Button>
            )}

            {pourId &&
              canStop &&
              formData.PourStatus === 'In Progress' && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<StopCircleIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openStopDialog();
                  }}
                  disabled={saving || stopping}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {t('pourSetup.stop', 'Stop')}
                </Button>
              )}

            {pourId && showRecalcMaturity && (
              <Tooltip
                describeChild
                title={pourLocked ? t('pourSetup.recalcUnlockFirst', 'Unlock the pour to recalculate') : ''}
              >
                {/* A disabled button fires no mouse events: the span carries
                    the tooltip, and keeps a click off the summary toggle. */}
                <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalculateIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecalcConfirmOpen(true);
                    }}
                    disabled={!canRecalcMaturity || saving || stopping || recalculating}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {t('pourSetup.recalculateMaturity', 'Recalculate maturity')}
                  </Button>
                </span>
              </Tooltip>
            )}

            {isView ? (
              !pourLocked && canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/pours/${pourId}/edit`, { replace: true });
                  }}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {t('pourSetup.edit', 'Edit')}
                </Button>
              )
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  guardedNavigate(`/pours/${pourId}`, { replace: true });
                }}
                disabled={saving || stopping}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {t('pourSetup.view', 'View')}
              </Button>
            )}

            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transform: summaryOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        </Box>
        <Collapse in={summaryOpen}>
          {/* Inset to line up under the title (past the back arrow) */}
          <Box sx={{ pl: 5, pr: 1, pt: 0.5, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {pourDetail.PourCode || '-'}
              {projectName !== '-' ? ` · ${projectName}` : ''}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                gap: 2,
                mt: 1.5,
              }}
            >
              <MetricCard label={t('pourSetup.product', 'Product')} value={productDetail?.code} />
              <MetricCard label={t('pourSetup.started', 'Started')} value={fmtDateTime(formData.Started)} />
              <MetricCard label={t('pourSetup.setupBy', 'Setup By')} value={pourDetail.SetupBy} />
              <MetricCard label={t('pourSetup.timeZone', 'Time Zone')} value={zoneText} />
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Stepper — compact progress bar on mobile, full icon stepper on desktop.
          Every step is freely clickable (the pour already exists). */}
      {isMobile ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {STEPS[activeStep]}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {STEPS.map((label, idx) => (
              <Box
                key={label}
                onClick={() => goToStep(idx)}
                role="button"
                aria-label={`Go to ${label}`}
                sx={mobileProgressSegmentSx(idx <= activeStep, true)}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          nonLinear
          sx={{
            ...STEPPER_SX,
            // Every step of an existing pour is reachable. The create
            // wizard's solid grey ("not reached yet") read as disabled here,
            // so steps without a tick get a brand-green outline instead.
            '& .MuiStepIcon-root:not(.Mui-active):not(.Mui-completed)': {
              color: 'common.white',
              border: `2px solid ${BRAND_GREEN}`,
              borderRadius: '50%',
              '& .MuiStepIcon-text': { fill: BRAND_GREEN },
            },
          }}
        >
          {STEPS.map((label, idx) => (
            <Step
              key={label}
              completed={idx !== activeStep && isStepComplete(idx)}
              onClick={() => goToStep(idx)}
              sx={{ cursor: 'pointer' }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Step Content */}
      <Box sx={{ mb: 4, minHeight: 400 }}>{stepContent[activeStep]()}</Box>

      {/* Bottom Navigation — sticky thumb-reachable bar on mobile */}
      {isMobile ? (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mt: 3,
            py: 1.5,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={saving}
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: 'none', py: 1.25, borderColor: 'grey.400', color: 'text.primary', flexShrink: 0 }}
              >
                Back
              </Button>
            )}
            {(!isView || activeStep < STEPS.length - 1) && (
              <Tooltip describeChild title={basisBlock || ''}>
                <span style={{ flex: 1, display: 'flex' }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={saving || !!basisBlock}
                    fullWidth
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : !isView && activeStep === STEPS.length - 1 ? <SaveIcon /> : undefined}
                    sx={{ textTransform: 'none', py: 1.25, flex: 1, ...PRIMARY_ACTION_SX }}
                  >
                    {saving
                      ? 'Saving...'
                      : isView
                        ? 'Next'
                        : activeStep === STEPS.length - 1
                          ? 'Save pour'
                          : 'Save / Next'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="text"
              onClick={() => guardedNavigate('/pours')}
              disabled={saving}
              sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
            >
              Cancel / Return
            </Button>
            {isDirty && (
              <Button
                variant="text"
                size="small"
                color="warning"
                startIcon={<UndoIcon fontSize="small" />}
                onClick={handleDiscard}
                disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                {t('pourSetup.discard', 'Discard changes')}
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mt: 'auto',
            py: 3,
            backgroundColor: 'common.white',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 3,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saving}
              sx={{ textTransform: 'none', px: 5, py: 1.25, borderColor: 'grey.400', color: 'text.primary' }}
            >
              BACK
            </Button>
          )}
          {(!isView || activeStep < STEPS.length - 1) && (
            <Tooltip describeChild title={basisBlock || ''}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving || !!basisBlock}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : !isView && activeStep === STEPS.length - 1 ? <SaveIcon /> : undefined}
                  sx={{ textTransform: 'none', px: 6, py: 1.25, minWidth: 200, ...PRIMARY_ACTION_SX }}
                >
                  {saving
                    ? 'Saving...'
                    : isView
                      ? 'NEXT'
                      : activeStep === STEPS.length - 1
                        ? 'SAVE'
                        : 'SAVE / NEXT'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="text"
            onClick={() => guardedNavigate('/pours')}
            disabled={saving}
            sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
          >
            Cancel / Return
          </Button>
          {isDirty && (
            <Button
              variant="text"
              size="small"
              color="warning"
              startIcon={<UndoIcon fontSize="small" />}
              onClick={handleDiscard}
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {t('pourSetup.discard', 'Discard changes')}
            </Button>
          )}
        </Box>
      )}

      {/* Stop-pour confirmation dialog */}
      <Dialog
        open={stopConfirmOpen}
        onClose={() => {
          if (!stopping) {
            setStopConfirmOpen(false);
            setStopTankCount(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={stopping}
      >
        <DialogTitle>{t('pourSetup.stopConfirmTitle', 'Stop Pour')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {t('pourSetup.stopConfirmBody', {
              code: formData.PourCode || formData.PourName || pourId,
            })}
          </DialogContentText>
          {stopTankCount !== null && stopTankCount > 0 && (
            <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
              {t('pourSetup.stopConfirmTankNote', { count: stopTankCount })}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStopConfirmOpen(false);
              setStopTankCount(null);
            }}
            disabled={stopping}
          >
            {t('pourSetup.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleStopConfirm}
            disabled={stopping}
            startIcon={
              stopping ? <CircularProgress size={20} color="inherit" /> : <StopCircleIcon />
            }
          >
            {stopping
              ? t('pourSetup.stopping', 'Stopping...')
              : t('pourSetup.confirmStop', 'Stop Pour')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate-maturity confirmation dialog */}
      <Dialog
        open={recalcConfirmOpen}
        onClose={() => {
          if (!recalculating) setRecalcConfirmOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={recalculating}
      >
        <DialogTitle>{t('pourSetup.recalcConfirmTitle', 'Recalculate maturity')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'pourSetup.recalcConfirmBody',
              "Recalculate maturity for every location from its recorded temperatures, using this pour's concrete start times and maturity inputs. Targets already met are marked at the time they were reached. No emails are sent.",
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalcConfirmOpen(false)} disabled={recalculating}>
            {t('pourSetup.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRecalcConfirm}
            disabled={recalculating}
            startIcon={
              recalculating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />
            }
          >
            {recalculating
              ? t('pourSetup.recalculating', 'Recalculating...')
              : t('pourSetup.confirmRecalc', 'Recalculate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={recalcResult !== null}
        autoHideDuration={8000}
        onClose={(_e, reason) => {
          if (reason !== 'clickaway') setRecalcResult(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={recalcResult?.ok === false ? 'error' : 'success'}
          variant="filled"
          onClose={() => setRecalcResult(null)}
        >
          {recalcResult?.text}
        </Alert>
      </Snackbar>

      {/* ── Add Project Dialog ── */}
      <Dialog open={addProjectOpen} onClose={resetProjectDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Project Name"
              value={newProjectForm.name}
              onChange={(e) => setNewProjectForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <Autocomplete
              freeSolo
              fullWidth
              options={clientOptions ?? []}
              value={newProjectForm.client || null}
              inputValue={newProjectForm.client}
              onInputChange={(_, value) => setNewProjectForm((p) => ({ ...p, client: value }))}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Select or type a client" />
              )}
            />
            <AzureAddressAutocomplete
              label="Address"
              value={newProjectForm.address}
              placeholder="e.g. 123 Main Street, Sydney NSW 2000"
              onChange={(address, lat, lon) => {
                setNewProjectForm((p) => ({ ...p, address }));
                if (lat && lon) setNewProjectLatLon({ lat, lon });
              }}
            />
            <Autocomplete
              fullWidth
              options={TIMEZONES}
              getOptionLabel={(option: TimezoneOption) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              value={TIMEZONES.find((tz) => tz.value === newProjectForm.timezone) || null}
              onChange={(_, selected) => setNewProjectForm((p) => ({ ...p, timezone: selected?.value || '' }))}
              renderInput={(params) => <TextField {...params} label="Timezone" />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetProjectDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddProject}
            variant="contained"
            disabled={!newProjectForm.name.trim() || addProjectSaving}
            sx={{ textTransform: 'none' }}
          >
            {addProjectSaving ? 'Adding...' : 'Add Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Product Dialog ── */}
      <Dialog open={addProductOpen} onClose={resetProductDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add New Product / Recipe</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="Product Code"
                value={newProductForm.code}
                onChange={(e) => setNewProductForm((p) => ({ ...p, code: e.target.value }))}
                fullWidth
                required
                autoFocus
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                label={labelWithUnit('Strength Grade', ownStrengthUnit)}
                value={newProductForm.strength_grade}
                onChange={(e) => setNewProductForm((p) => ({ ...p, strength_grade: e.target.value }))}
                type="number"
                fullWidth
              />
            </Box>
            <TextField
              label="Description"
              value={newProductForm.description}
              onChange={(e) => setNewProductForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
            <FormControl fullWidth>
              <InputLabel>Supplier / Batch Plant</InputLabel>
              <Select
                value={newProductForm.supplier_plant_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, supplier_plant_id: e.target.value }))}
                label="Supplier / Batch Plant"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {supplierPlants.map((plant) => (
                  <MenuItem key={plant.supplier_plant_id} value={plant.supplier_plant_id}>
                    {plant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: -1 }}>Defaults</Typography>
            <FormControl fullWidth>
              <InputLabel>Mass Concrete Default</InputLabel>
              <Select
                value={newProductForm.mass_concrete_default_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, mass_concrete_default_id: e.target.value }))}
                label="Mass Concrete Default"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.mass_concrete_defaults || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Evaporation Default</InputLabel>
              <Select
                value={newProductForm.evaporation_default_id}
                onChange={(e: SelectChangeEvent) => setNewProductForm((p) => ({ ...p, evaporation_default_id: e.target.value }))}
                label="Evaporation Default"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(lookups?.evaporation_defaults || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetProductDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            disabled={!newProductForm.code.trim() || addProductSaving}
            sx={{ textTransform: 'none' }}
          >
            {addProductSaving ? 'Adding...' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Batch Plant Dialog ── */}
      <Dialog open={addPlantOpen} onClose={resetPlantDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Batch Plant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Batching Plant Name"
              value={newPlantForm.name}
              onChange={(e) => setNewPlantForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              autoFocus
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Ready Mix Supplier"
              value={newPlantForm.ready_mix_supplier}
              onChange={(e) => setNewPlantForm((p) => ({ ...p, ready_mix_supplier: e.target.value }))}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
            <AzureAddressAutocomplete
              label="Address"
              value={newPlantForm.address}
              placeholder="e.g. 4/3 Ascot Road, Ballina NSW 2478"
              onChange={(address, lat, lon) => {
                setNewPlantForm((p) => ({
                  ...p,
                  address,
                  ...(lat && lon ? { latitude: lat, longitude: lon } : {}),
                }));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetPlantDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddPlant}
            variant="contained"
            disabled={!newPlantForm.name.trim() || addPlantSaving}
            sx={{ textTransform: 'none' }}
          >
            {addPlantSaving ? 'Adding...' : 'Add Batch Plant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PourSetup;
